import {useState, useEffect, useCallback} from 'react';
import type {KeyboardEvent} from 'react';

import {Popover} from '../../popover';
import {AutocompleteInput} from '../../autocomplete-input';
import {AutocompleteList} from '../../autocomplete-list';

import type {Props} from './types';
import styles from './styles.module.css';

const MIN_QUERY_LENGTH_TO_TRIGGER = 1;

export const Autocomplete = <Chip, ListItem>({
    items,
    getLabelFromChip,
    getKeyFromChip,
    renderItem,
    getKeyFromItem,
    getLabelFromItem,
    isLoading,
    errorMessage,
    getIconFromChip,
    minQueryLengthToTrigger = MIN_QUERY_LENGTH_TO_TRIGGER,
    textFieldProps,
    convertItemToChip,
    onChange,
    onInputChange,
}: Props<Chip, ListItem>) => {
    const [chips, setChips] = useState<Chip[]>([]);
    const [query, setQuery] = useState('');
    const [isAutocompleteListVisible, setIsAutocompleteListVisible] = useState(false);

    const hideAutocompleteList = () => {
        setIsAutocompleteListVisible(false);
    };

    const updateQueryOnChange = (nextQuery: string) => {
        setQuery(nextQuery);
    };

    const handlePopoverClose = () => {
        if (query.length < minQueryLengthToTrigger) {
            hideAutocompleteList();
        }
    };

    useEffect(() => {
        if (query.length >= minQueryLengthToTrigger) {
            if (!isAutocompleteListVisible) {
                setIsAutocompleteListVisible(true);
            }
        }

        if (!query && isAutocompleteListVisible) {
            hideAutocompleteList();
        }
    }, [isAutocompleteListVisible, minQueryLengthToTrigger, query]);

    useEffect(() => {
        if (query.length >= minQueryLengthToTrigger) {
            onInputChange(query);
        }
    }, [minQueryLengthToTrigger, onInputChange, query]);

    const addChip = useCallback(
        (item: ListItem) => {
            setQuery('');
            const nextChip = convertItemToChip ? convertItemToChip(item) : item;
            const nextChips = chips.concat(nextChip as Chip);

            setChips(nextChips);
            onChange(nextChips);
        },
        [chips, convertItemToChip, onChange],
    );

    const removeChip = useCallback(
        (removedChipKey) => {
            const nextChips = chips.filter((prevChip) => getKeyFromChip(prevChip) !== removedChipKey);

            setChips(nextChips);
            onChange(nextChips);
        },
        [chips, getKeyFromChip, onChange],
    );

    const handleBackspacePress = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== 'Backspace' || chips.length === 0 || query !== '') {
                return;
            }

            const nextChips = chips.slice(0, -1);

            setChips(nextChips);
            onChange(nextChips);
        },
        [chips, onChange, query],
    );

    return (
        <div>
            <div className={styles.autocompleteInputContainer}>
                <AutocompleteInput<Chip>
                    query={query}
                    onChange={updateQueryOnChange}
                    chips={chips}
                    getKeyFromChip={getKeyFromChip}
                    getLabelFromChip={getLabelFromChip}
                    onChipRemove={removeChip}
                    errorMessage={errorMessage}
                    getIconFromChip={getIconFromChip}
                    textFieldProps={{
                        ...textFieldProps,
                        onKeyDown: handleBackspacePress,
                    }}
                />
            </div>
            <Popover isOpen={isAutocompleteListVisible && !errorMessage} onClose={handlePopoverClose}>
                <AutocompleteList<ListItem>
                    query={query}
                    renderItem={renderItem}
                    items={items}
                    getKeyFromItem={getKeyFromItem}
                    getLabelFromItem={getLabelFromItem}
                    isLoading={isLoading}
                    onSelect={addChip}
                />
            </Popover>
        </div>
    );
};
